import { MultiSelectChangeReason } from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { useMemoizedRules } from 'components/forms/inputs/rhf-inputs/useMemoizedRules';
import { PaginatedListProps } from 'components/forms/inputs/rhf-inputs/usePaginatedList';
import { useSectionWideClass } from 'components/section-edit/SectionEdit';
import { ReactNode, useEffect, useMemo } from 'react';
import { Control, useController, useForm } from 'react-hook-form';
import { MatterForm } from 'types/matters';
import { PracticeArea } from 'types/practice-areas';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';

type Props = {
    control: Control<MatterForm>;
    required: boolean;
    paginated: PaginatedListProps<PracticeArea>;
    /** Told of every area that leaves the case, as the multi-select tells of a chip removed or of a clear. */
    onPracticeAreaChange: (practiceArea: PracticeArea | null, reason: MultiSelectChangeReason) => void;
    /** Told of the area picked, once the areas it replaces have been told of. */
    onPracticeAreaSelect: (practiceArea: PracticeArea) => void;
    getSelectedValueEndAdornment?: (option: PracticeArea) => ReactNode;
};

/**
 * The case form's practice-area field where a case takes one area. The form keeps its `practiceAreas` list: the
 * select shows the first of it and a pick replaces the whole list, so a case that holds several keeps them all
 * until the user picks.
 */
export function SinglePracticeAreaInput({
    control,
    required,
    paginated,
    onPracticeAreaChange,
    onPracticeAreaSelect,
    getSelectedValueEndAdornment,
}: Props): JSX.Element {
    const rules = useMemoizedRules<MatterForm, 'practiceAreas'>({
        required,
        requiredMessageId: 'validation.choose-option',
    });
    const {
        field: { value: stored, onChange: setStored, ref },
        fieldState: { error },
    } = useController({ control, name: 'practiceAreas', rules });
    const shown = stored[0] ?? null;
    // In a record section the read row takes the whole row once the case has an area, and the multi-select with
    // it: so does the select.
    const wideClass = useSectionWideClass(control, 'practiceAreas');
    // The select writes the option picked to its field, and the case form's field is a list, so it gets a
    // one-field form of its own. Bound to `practiceAreas.0` it would leave `[undefined]` in an empty list.
    const values = useMemo(() => ({ practiceArea: shown }), [shown]);
    const single = useForm<{ practiceArea: PracticeArea | null }>({ values });
    const { setError, clearErrors, setFocus } = single;

    // The case form validates the list and the select shows its error. A change of the area shown resets the
    // select's own form, error included, so the error is set again then.
    useEffect(() => {
        if (error) {
            setError('practiceArea', { type: error.type, message: error.message });
        } else {
            clearErrors('practiceArea');
        }
    }, [error, shown, setError, clearErrors]);

    // A failed save focuses the first field in error, which for the list is the select.
    useEffect(() => {
        ref({ focus: () => setFocus('practiceArea') });
    }, [ref, setFocus]);

    return (
        <DynamicSelectInput
            setValue={single.setValue}
            control={single.control}
            label="practice-area"
            placeholder="matters.select-practice-area"
            name="practiceArea"
            getOptionLabel={(option) => formatPracticeAreaLabel(option)}
            getOptionIdentifier="id"
            required={required}
            paginated={paginated}
            wrapperClassName={wideClass}
            onChange={(picked) => {
                setStored(picked ? [picked] : []);
                if (!picked) {
                    onPracticeAreaChange(null, 'clear');
                    return;
                }
                stored
                    .filter((area) => area.id !== picked.id)
                    .forEach((area) => onPracticeAreaChange(area, 'removeOption'));
                onPracticeAreaSelect(picked);
            }}
            getSelectedValueEndAdornment={getSelectedValueEndAdornment}
        />
    );
}
